/**
 * SearchResults
 *
 * Dropdown overlay that shows search results below the search input.
 * Groups results by mission, highlights matched terms, and supports
 * keyboard navigation (arrow keys, Enter, Escape).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Button } from '@agion/ui'
import { Loader2, MessageSquare, Search, X } from 'lucide-react'
import type { SearchResult } from '../../services/search-api'

interface SearchResultsProps {
  results: SearchResult[]
  total: number
  isSearching: boolean
  error: string | null
  query: string
  hostsQueried: number
  hostsResponded: number
  onClose: () => void
}

/** Group results by mission ID for cleaner display */
function groupByMission(results: SearchResult[]): Map<string, SearchResult[]> {
  const groups = new Map<string, SearchResult[]>()
  for (const result of results) {
    const key = result.mission_id || 'unknown'
    const existing = groups.get(key) || []
    existing.push(result)
    groups.set(key, existing)
  }
  return groups
}

/** Highlight matched query in snippet text */
function highlightSnippet(snippet: string, query: string): React.ReactNode {
  if (!query || query.length < 2) return snippet

  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
  const parts = snippet.split(regex)

  return parts.map((part, i) =>
    regex.test(part) ? (
      <mark key={i} className="bg-yellow-200 dark:bg-yellow-500/30 text-inherit rounded-sm px-0.5">
        {part}
      </mark>
    ) : (
      <span key={i}>{part}</span>
    )
  )
}

export function SearchResults({
  results,
  total,
  isSearching,
  error,
  query,
  hostsQueried,
  hostsResponded,
  onClose,
}: SearchResultsProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const containerRef = useRef<HTMLDivElement>(null)
  const [selectedIndex, setSelectedIndex] = useState(-1)

  const handleResultClick = useCallback(
    (result: SearchResult) => {
      if (result.mission_id) {
        const path = result.execution_id
          ? `/mission/${result.mission_id}?execution=${result.execution_id}`
          : `/mission/${result.mission_id}`
        navigate(path)
        onClose()
      }
    },
    [navigate, onClose]
  )

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1))
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex((prev) => Math.max(prev - 1, -1))
      }
      if (e.key === 'Enter' && selectedIndex >= 0 && selectedIndex < results.length) {
        e.preventDefault()
        const selected = results[selectedIndex]
        if (selected) handleResultClick(selected)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [results, selectedIndex, handleResultClick, onClose])

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(-1)
  }, [results])

  if (!query || query.length < 2) return null

  const grouped = groupByMission(results)

  return (
    <div
      ref={containerRef}
      className="absolute top-full left-1/2 -translate-x-1/2 w-[32rem] max-h-[28rem] mt-1 z-50
        bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
        rounded-lg shadow-lg overflow-hidden flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 dark:border-slate-700">
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {isSearching ? (
            <span className="flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" />
              {t('chat:search.searching', 'Searching...')}
            </span>
          ) : error ? (
            <span className="text-rose-500">{error}</span>
          ) : (
            t('chat:search.resultsCount', '{{total}} results across {{hosts}} hosts', {
              total,
              hosts: hostsResponded,
            })
          )}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="p-0.5 h-auto"
        >
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>

      {/* Results */}
      <div className="overflow-y-auto flex-1">
        {results.length === 0 && !isSearching && !error && (
          <div className="py-8 text-center text-sm text-slate-400 dark:text-slate-500">
            <Search className="w-8 h-8 mx-auto mb-2 opacity-30" />
            {t('chat:search.noResults', 'No messages found')}
          </div>
        )}

        {Array.from(grouped.entries()).map(([missionId, missionResults]) => {
          let flatIndex = 0
          // Calculate flat index offset for this group
          for (const [, groupResults] of grouped) {
            if (groupResults === missionResults) break
            flatIndex += groupResults.length
          }

          return (
            <div key={missionId}>
              {missionId !== 'unknown' && (
                <div className="px-3 py-1.5 text-[11px] font-medium text-slate-400 dark:text-slate-500 bg-slate-50 dark:bg-slate-800/50 sticky top-0">
                  {missionResults[0]?.mission_name || missionId.slice(0, 8)}
                </div>
              )}
              {missionResults.map((result, i) => {
                const globalIdx = flatIndex + i
                return (
                  <Button
                    key={result.id}
                    variant="ghost"
                    onClick={() => handleResultClick(result)}
                    className={`w-full text-left px-3 py-2 h-auto rounded-none border-b border-slate-50 dark:border-slate-700/50
                      ${globalIdx === selectedIndex ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                  >
                    <div className="w-full">
                      <div className="flex items-center gap-2 mb-0.5">
                        <MessageSquare className="w-3 h-3 text-slate-400 flex-shrink-0" />
                        <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                          {result.role === 'assistant'
                            ? (result.agent_name || t('chat:search.agent', 'Agent'))
                            : (result.user_name || t('chat:search.user', 'User'))}
                        </span>
                        <span className="text-[10px] text-slate-400 dark:text-slate-500 ml-auto">
                          {new Date(result.created_at).toLocaleString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                      <p className="text-xs text-slate-700 dark:text-slate-300 line-clamp-2 font-normal">
                        {highlightSnippet(result.snippet, query)}
                      </p>
                    </div>
                  </Button>
                )
              })}
            </div>
          )
        })}
      </div>

      {/* Footer */}
      {hostsQueried > 0 && !isSearching && (
        <div className="px-3 py-1.5 border-t border-slate-100 dark:border-slate-700 text-[10px] text-slate-400 dark:text-slate-500">
          {t('chat:search.hostsInfo', '{{responded}}/{{queried}} hosts responded', {
            responded: hostsResponded,
            queried: hostsQueried,
          })}
          {' · '}
          {t('chat:search.keyboardHint', '↑↓ navigate · Enter to open · Esc to close')}
        </div>
      )}
    </div>
  )
}
